import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { Agent } from '@/hooks/useAgents';
import { useAxios } from '@/hooks/useAxios';

export interface ActiveDirectoryCredential {
  credentialId: string;
  name: string;
  accountKey: string;
  username: string;
  type: string;
  category: string;
  created: string;
  updated: string;
}

interface AddCredentialParams {
  label: string;
  username: string;
  password: string;
  domain: string;
  resourceKey: string;
}

interface DeleteCredentialParams {
  credentialId: string;
}

export const useActiveDirectoryCredentials = (agent: Agent) => {
  const axios = useAxios();
  const queryClient = useQueryClient();

  // Query to fetch credentials for this agent
  const credentialsQuery = useQuery({
    queryKey: ['ad-credentials', agent.client_id],
    queryFn: async () => {
      // Query Neo4j for credentials linked to this agent/asset
      const response = await axios.post('/my', {
        node: {
          labels: ['Credential'],
          filters: [
            { field: 'accountKey', operator: '=', value: agent.client_id },
            { field: 'type', operator: '=', value: 'active-directory' },
          ],
        },
      });

      // The /my endpoint returns a GraphSearch object that gets flattened:
      // { credentials: [...], count: N }
      // The backend marshalSearch function moves items.credentials to the top level
      const data = response.data;

      if (data?.credentials && Array.isArray(data.credentials)) {
        return data.credentials as ActiveDirectoryCredential[];
      }

      console.warn('Unexpected response format from /my endpoint:', data);
      return [];
    },
    enabled: !!agent.client_id,
  });

  // Mutation to add a new credential
  const addCredential = useMutation({
    mutationFn: async (params: AddCredentialParams) => {
      const response = await axios.post('/broker', {
        Category: 'env-integration',
        Type: 'active-directory',
        Format: ['env'],
        Operation: 'add',
        ResourceKey: params.resourceKey,
        Parameters: {
          label: params.label,
          username: params.username,
          password: params.password,
          domain: params.domain,
        },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['ad-credentials', agent.client_id],
      });
      toast.success('Credential added successfully');
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message || 'Failed to add credential';
      toast.error(message);
    },
  });

  // Mutation to delete a credential
  const deleteCredential = useMutation({
    mutationFn: async (params: DeleteCredentialParams) => {
      const response = await axios.post('/broker', {
        CredentialID: params.credentialId,
        Type: 'active-directory',
        Operation: 'delete',
        Format: ['env'],
        Category: 'env-integration',
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['ad-credentials', agent.client_id],
      });
      toast.success('Credential deleted successfully');
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message || 'Failed to delete credential';
      toast.error(message);
    },
  });

  return {
    credentials: Array.isArray(credentialsQuery.data)
      ? credentialsQuery.data
      : [],
    isLoading: credentialsQuery.isLoading,
    isError: credentialsQuery.isError,
    addCredential,
    deleteCredential,
  };
};
